

import { useState } from "react";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { IconButton } from "@/components/Buttons/IconButton";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { CapacityDecisionModal } from "@/components/CapacityDecisionModal/CapacityDecisionModal";
import { useToast } from "@/components/Toast/Toast";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { updateGuests } from "@/lib/api/handlers/GuestHandling";
import {
  approvalBlockedGuestIds,
  capacityContextOf,
  CapacityContext,
  throwServerError,
} from "@/lib/api/errors/ErrorHandler";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { AcceptanceStateChip } from "@/components/AcceptanceStateChip/AcceptanceStateChip";
import { GuestType } from "@/types/enums/GuestType";
import { Guest } from "@/types/Guest";
import {
  armGuestReconcile,
  armCoarseReconcile,
} from "@/lib/guest-reconcile/reconcileScheduler";
import { dropSelfWrittenFields } from "@/lib/guest-reconcile/selfWrittenFields";
import { readBackGuestVerdict } from "@/lib/guest-reconcile/readBackAcceptance";
import { useGuestAutomationGate } from "@/hooks/useGuestAutomationGate";
import { deriveLifecycleTriggers } from "@/lib/utils/derive-lifecycle-triggers";
import { dropGuestFromRequestsCache } from "@/lib/query/requestsCache";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";

type AcceptanceActionsProps = {
  guest: Guest;
  eventId?: string;
  archived: boolean;
  acceptanceState: Guest["acceptanceState"];
};

/** Detail shape of the `guestAcceptanceResolved` window event dispatched
 *  after an in-popup approve/decline. RegistrantsProvider/CompanionsProvider
 *  listen and mirror the TCCAction bookkeeping (row removal, badge/total). */
export type GuestAcceptanceResolvedDetail = {
  eventId: string;
  guestId: string;
  state: AcceptanceState;
  /**
   * Which request list the guest left. Only the MOUNTED tab's provider can do
   * its own badge bookkeeping, so `ClientWrapper` uses this to cover the
   * cross-tab case (resolving a registrant from the popup while the companions
   * tab is open). Absent when the guest's type is unknown — then nobody
   * decrements, which is the old, conservative behaviour.
   */
  kind?: "registrants" | "companions";
};

/**
 * Approve/decline for a PENDING request, inside the guest-detail popup — the
 * same decision the row's action cell (TCCAction) offers, without leaving the
 * detail view. Self-contained on purpose: the popup can be opened from pages
 * where RegistrantsProvider/CompanionsProvider don't exist (guestlist,
 * seating), so it writes via `updateGuests` itself and broadcasts the result
 * as a `guestAcceptanceResolved` window event; the requests-page providers
 * listen and drop the row + fix the counts.
 */
export const AcceptanceActions: React.FC<AcceptanceActionsProps> = ({
  guest,
  eventId,
  archived,
  acceptanceState,
}) => {
  const t = useTranslations("guestDetails");
  const tRequests = useTranslations("eventGuestsRequests");
  const tErr = useTranslations("errors");
  const { can } = useCapabilities();
  const { guard } = usePreviewWriteGuard();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  // Which fields could an enabled rule write on the triggers this button
  // fires? Costs nothing beyond what the page already fetches — the workflow
  // list shares its query key with the automation page.
  const { mayRun, watchedFieldsFor } = useGuestAutomationGate(eventId);

  const [loading, setLoading] = useState<"accept" | "decline" | null>(null);
  const [resolved, setResolved] = useState<AcceptanceState | null>(null);
  // Event full → the shared 3-option capacity popup (waitlist / admit over
  // the cap / cancel), exactly like accepting from the requests table.
  const [capacityCtx, setCapacityCtx] = useState<CapacityContext | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  /** The RAW failure of the last popup choice; the popup translates it and
   *  stays open (M-10). Reset where the popup opens (`submit`). */
  const [decisionError, setDecisionError] = useState<unknown>(null);

  const guestId = guest.guestId;

  // Just resolved from here: a passive chip replaces the buttons until the
  // popup's own refetch swaps the acceptanceState prop over (then this
  // component renders nothing, like any non-pending guest).
  if (resolved && acceptanceState === AcceptanceState.PENDING) {
    // Was a hand-rolled binary chip: ACCEPTED → green, everything else →
    // error-red + ✕. Survivable only while `resolved` held the value we ASKED
    // for; once the authoritative read-back began writing what the server
    // stored, a successful waitlist reroute (SYSTEM) rendered as a rejection.
    //
    // `AcceptanceStateChip` already fixed precisely this for the requests grid
    // — SYSTEM has its own colour there, and its docstring names the same bug.
    // Reusing it keeps the popup and the grid from drifting apart the next time
    // either changes.
    return <AcceptanceStateChip state={resolved} testId="acceptance-resolved-chip" />;
  }

  if (
    !eventId ||
    !guestId ||
    archived ||
    acceptanceState !== AcceptanceState.PENDING ||
    !can("guests:write")
  ) {
    return null;
  }

  // The WHOLE error: the per-row code is the reason of a guest batch 400
  // (M-01c), not the envelope's "Some guests could not be saved.".
  const surfaceError = (error: unknown) => {
    showToast(describeBatchUpdateError(tErr, error, tErr("unknown")), "error");
  };

  /**
   * `"ok"` — written; `"reblocked"` — the capacity popup is opened with a
   * fresh context. Both used to resolve `undefined`, so a caller could not
   * tell "done" from "ask the admin again" (M-10). A failure throws — and so
   * does a capacity block on the popup's OWN resubmit (`fromPopup`): that is
   * the answer to the capacity question, shown in the popup like the twin
   * providers do, not a silent re-open (audit r1 A-6).
   */
  const submit = async (
    payload: Guest,
    state: AcceptanceState,
    fromPopup = false,
  ): Promise<"ok" | "reblocked"> => {
    const response = await updateGuests(eventId, [payload]);
    if (
      typeof response === "object" &&
      response !== null &&
      "isServerError" in response &&
      response.isServerError === true
    ) {
      if (state === AcceptanceState.ACCEPTED && !fromPopup) {
        const ctx = capacityContextOf(response);
        if (ctx && approvalBlockedGuestIds(response).includes(guestId)) {
          setDecisionError(null);
          setCapacityCtx(ctx);
          return "reblocked";
        }
      }
      throwServerError(response);
    }

    const resolvedKind =
      guest?.guestType === GuestType.COMPANION
        ? ("companions" as const)
        : guest?.guestType === GuestType.MAIN_GUEST
          ? ("registrants" as const)
          : undefined;

    setResolved(state);
    // `state` is what we ASKED for, and on the forceWaitlist lane that is not
    // necessarily what the server stored: the reroute to WAITLISTED/SYSTEM
    // only fires while the capacity gate would still block, and capacity can
    // free up between the dialog rendering and the click. The chip below reads
    // `resolved`, so a guess here shows the admin a verdict nobody gave.
    if (payload.forceWaitlist === true) {
      void readBackGuestVerdict(eventId, guestId).then((stored) => {
        if (stored) setResolved(stored.acceptanceState);
      });
    }
    window.dispatchEvent(
      new CustomEvent<GuestAcceptanceResolvedDetail>(
        "guestAcceptanceResolved",
        { detail: { eventId, guestId, state, kind: resolvedKind } },
      ),
    );
    // An approval/denial from inside the popup fires the same REQUEST_*/
    // COMPANION_* triggers as the requests grid, and any UPDATE_GUEST pipe on
    // them lands after this response. Watch this guest so every surface showing
    // it (grid, sidebar, the popup itself) picks up the server's value.
    //
    // Watch the fields the RULES target, not a fixed pair. Hardcoding
    // `state`/`acceptanceState` here meant a rule writing anything else — a
    // custom field, `guestType`, `company` — armed a watch that could never be
    // satisfied: ten polls over 45s, no broadcast, and the popup, the sidebar
    // and the grid all kept the pre-automation value. The providers ask the
    // gate and fall back to a coarse pulse; this lane never did.
    const triggerList = deriveLifecycleTriggers(
      { acceptanceState: guest?.acceptanceState, guestType: guest?.guestType },
      { acceptanceState: state },
      false,
    );
    if (mayRun(triggerList)) {
      // Drop the fields THIS request may itself move. `isSatisfied` is a
      // `some`, so one watch that flips on our own write ends the wave for
      // this guest and takes the correct sibling watches with it.
      const fields = dropSelfWrittenFields(watchedFieldsFor(triggerList), {
        forceWaitlist: payload.forceWaitlist,
        // The server's approval sync moves `state` on every PENDING → ACCEPTED
        // write, not only on the waitlist reroute — so this lane needs the same
        // treatment, and it is the COMMON one.
        approvesPendingRequest:
          guest?.acceptanceState === AcceptanceState.PENDING &&
          state === AcceptanceState.ACCEPTED,
      });
      if (fields.length === 0) {
        // The workflow list is not loaded yet, so we cannot name the fields.
        // A coarse pulse is honest; a watch that cannot satisfy is the same as
        // no reconcile at all.
        armCoarseReconcile(eventId);
      } else {
        armGuestReconcile({
          eventId,
          watches: new Map([
            [
              guestId,
              fields.map(({ field, isAdditional }) => ({
                field,
                isAdditional,
                // Everything reaching here is untouched by this write —
                // `dropSelfWrittenFields` removed the rest — so the stored
                // value is a valid baseline. The branch that used to sit here
                // special-cased `acceptanceState`, which `watchedFieldsFor`
                // can never emit non-additional, so it never ran and `state`
                // was left with a baseline our own write could move.
                fromRaw: isAdditional
                  ? (guest?.additionalFields as
                      | Record<string, unknown>
                      | undefined)?.[field]
                  : (guest as unknown as Record<string, unknown> | undefined)?.[
                      field
                    ],
              })),
            ],
          ]),
        });
      }
    }
    // Drop the row from the cached requests snapshots. The event above only
    // reaches a MOUNTED provider, and on the requests page exactly one of the
    // two tabs is mounted — resolving a registrant while the companions tab is
    // open (reachable through the Verknüpfung cell) reached no listener at all,
    // so the request stayed in the snapshot and came back actionable. Writing
    // the cache here is the one place both tabs and both dispatch sites share.
    //
    // The hint makes the COUNT drop even when the row is not on the cached
    // first page (the admin was on page 2+), matching the unconditional
    // decrement the grid does. It is derived from the guest's own type rather
    // than assumed: a wrong hint would decrement the other tab's count, so an
    // unknown type deliberately passes nothing and only fixes the rows.
    dropGuestFromRequestsCache(queryClient, eventId, guestId, resolvedKind);
    // The requests/guestlist snapshots outlive this popup. Mounted providers
    // drop the row via the event above; refetchType "none" makes a later
    // remount refetch instead of resurrecting the actioned row from cache.
    void queryClient.invalidateQueries({
      queryKey: ["guestsRequests", eventId],
      refetchType: "none",
    });
    void queryClient.invalidateQueries({
      queryKey: ["guestlist", eventId],
      refetchType: "none",
    });
    void queryClient.invalidateQueries({
      queryKey: ["guestDetailGuest", eventId, guestId],
      refetchType: "none",
    });
    showToast(
      tRequests(
        state === AcceptanceState.ACCEPTED ? "toastApproved" : "toastDeclined",
      ),
      "success",
    );
    return "ok";
  };

  const handleDecide = async (state: AcceptanceState) => {
    setLoading(state === AcceptanceState.ACCEPTED ? "accept" : "decline");
    try {
      await submit({ guestId, acceptanceState: state } as Guest, state);
    } catch (error) {
      surfaceError(error);
    } finally {
      setLoading(null);
    }
  };

  // Re-submit the accept after the admin's capacity choice — "waitlist"
  // routes the request onto the waitlist, "normal" admits it over the cap.
  const resubmitAccept = async (decision: "waitlist" | "normal") => {
    setDecisionLoading(true);
    const payload: Guest =
      decision === "waitlist"
        ? { guestId, acceptanceState: AcceptanceState.ACCEPTED, forceWaitlist: true }
        : { guestId, acceptanceState: AcceptanceState.ACCEPTED, overrideRules: true };
    try {
      // Close only on a write that went through (R3, M-10); a re-block throws
      // here and is shown in the popup.
      if ((await submit(payload, AcceptanceState.ACCEPTED, true)) === "ok") {
        setCapacityCtx(null);
      }
    } catch (error) {
      // In the popup, which stays open — no second channel.
      setDecisionError(error);
    } finally {
      setDecisionLoading(false);
    }
  };

  return (
    <>
      <IconButton
        icon={IconX}
        width="w-max"
        backgroundColor="surface-primary"
        padding="px-3 py-2"
        text={t("declineRequest")}
        space="space-x-1.5"
        color="font-error"
        iconSize={16}
        border="border outline-quaternary"
        loading={loading === "decline"}
        disabled={loading !== null}
        testId="acceptance-decline"
        onClickAction={guard(() => void handleDecide(AcceptanceState.DECLINED))}
      />
      <PrimaryButton
        icon={IconCheck}
        iconSize={16}
        size="small"
        width="w-max"
        // Match the Decline IconButton's box (px-3 py-2, body-2-medium) so the
        // pair reads as one control group.
        paddingOverride="px-3 py-2"
        text={t("approveRequest")}
        loading={loading === "accept"}
        disabled={loading !== null}
        testId="acceptance-approve"
        onClick={guard(() => void handleDecide(AcceptanceState.ACCEPTED))}
      />
      {capacityCtx && (
        <CapacityDecisionModal
          context={capacityCtx}
          kind="accept"
          isLoading={decisionLoading}
          onWaitlist={() => void resubmitAccept("waitlist")}
          onNormal={() => void resubmitAccept("normal")}
          onCancel={() => setCapacityCtx(null)}
          error={decisionError ?? undefined}
        />
      )}
    </>
  );
};
